import { createContext, useContext, useState } from 'react';

import {
  Theme,
  ThemeProvider as BaseThemeProvider,
} from '@mui/material/styles';
import StyledEngineProvider from '@mui/styled-engine/StyledEngineProvider';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { SnackbarProvider } from 'notistack';

import { AdapterDateFns } from '../components/pickers/adapter-date-fns';
import LightTheme from './schemes/light-theme';

export { default as theme } from './schemes/light-theme';

export type ThemeKeys = 'light';

interface ThemeContextProps {
  currentTheme: ThemeKeys;
  toggleTheme: (theme: ThemeKeys) => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeKeys>('light');

  const toggleTheme = (theme: ThemeKeys) => {
    setCurrentTheme(theme);
  };

  const themeMap: Record<ThemeKeys, Theme> = {
    light: LightTheme,
  };

  const theme = themeMap[currentTheme];

  return (
    <ThemeContext.Provider value={{ currentTheme, toggleTheme }}>
      <SnackbarProvider
        maxSnack={3}
        style={{ fontSize: 'small' }}
        domRoot={document.getElementById('snackbar-portal')!}
      >
        <StyledEngineProvider injectFirst>
          <BaseThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              {children}
            </LocalizationProvider>
          </BaseThemeProvider>
        </StyledEngineProvider>
      </SnackbarProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useCustomTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useCustomTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;
